import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import {
  ActivitiesV2Gateway,
  RequestForProposalGateway,
} from '@wilson/api/gateway';
import { ManyEntity, Pagination } from '@wilson/base';
import {
  ActivitiesJob,
  Activity,
  ActivityCategory,
  ActivitySelectionListItem,
  RequestForProposal,
  Shift,
  User,
} from '@wilson/interfaces';
import { TranslateActivityCategoryService } from '@wilson/activity-categories/data';
import { LocalizedDateTimeFormatPipe } from '@wilson/localization';
import { endOfMonth, startOfMonth, subMonths } from 'date-fns';
import { flatten } from 'lodash';
import { NzMessageService } from 'ng-zorro-antd/message';
import { Observable, catchError, combineLatest, map, of, tap } from 'rxjs';
import {
  ActivityInvoiceModel,
  ActivityViewModel,
  InvoiceDateTypeFilter,
  InvoiceFilters,
  InvoicingDataStateModel,
  InvoicingGridPageSizes,
  SelectedActivityChecks,
} from '../interfaces/invoicing-custom-types';
import { InvoiceDataHelperService } from './invoicing-data-helper.service';
import {
  InvoicingFetchShifts,
  InvoicingFilterOnlyServiceActivities,
  InvoicingSetActivityCategories,
  InvoicingSetActivityStatus,
  InvoicingSetAgreements,
  InvoicingSetCacheBlockSize,
  InvoicingSetClient,
  InvoicingSetDateTypeFilterIndex,
  InvoicingSetEmployeeOrgUnits,
  InvoicingSetEmployees,
  InvoicingSetJobs,
  InvoicingSetPageSize,
  InvoicingSetPagination,
  InvoicingSetPartners,
  InvoicingSetSearchString,
  InvoicingSetSelectedActivity,
  InvoicingSetShiftCategories,
  InvoicingSetShiftLabels,
  InvoicingSetShiftProfessions,
  InvoicingSetShiftProjects,
  InvoicingSetTimeRange,
  InvoicingSetUserLabels,
  SetSelectedActivities,
} from './invoicing-data.actions';
interface MappedActivitySelectionItems {
  data: ActivitySelectionListItem[];
}

const defaults: InvoicingDataStateModel = {
  activities: null,
  selectedActivities: [],
  selectedClientId: null,
  selectedClientName: null,
  selectedActivityStates: {
    isSameClient: false,
    doActivitiesHaveAgreements: false,
    doAgreementProductsHaveRules: false,
  },
  filters: {
    agreements: [],
    partners: [],
    jobId: [],
    shiftUserLabelIds: [],
    shiftLabelsIds: [],
    shiftProjectIds: [],
    profession: [],
    orgUnit: [],
    searchString: null,
    dateTypeFilterIndex: InvoiceDateTypeFilter.ByService,
    dateRange: {
      start: startOfMonth(subMonths(new Date(), 1)),
      end: endOfMonth(subMonths(new Date(), 1)),
    },
    employees: [],
    shiftCategories: [],
    activityCategories: [],
    showServiceActivities: false,
    invoiced: null,
  },
  pageNumber: 1,
  pageSize: 100,
  cacheBlockSize: 100,
  pagination: {
    currentPage: 1,
    numberOfPages: 0,
    pageSize: 0,
    totalRecords: 0,
  },
  pageSizes: [20, 50, 100],
};

@State<InvoicingDataStateModel>({
  name: 'invoicingData',
  defaults,
})
@Injectable()
export class InvoicingDataState {
  private readonly activitiesGateway = inject(ActivitiesV2Gateway);
  private readonly translate = inject(TranslateService);
  private readonly message = inject(NzMessageService);
  private readonly requestForProposalGatewayService = inject(
    RequestForProposalGateway,
  );
  private readonly invoiceDataHelperService = inject(InvoiceDataHelperService);
  private readonly translateActivityCategoryService = inject(
    TranslateActivityCategoryService,
  );

  private readonly localizedDateTimeFormat = inject(
    LocalizedDateTimeFormatPipe,
  );

  @Selector()
  static activities(
    state: InvoicingDataStateModel,
  ): ActivityViewModel[] | null {
    return state.activities;
  }

  @Selector()
  static getDateTypeFilterIndex(
    state: InvoicingDataStateModel,
  ): InvoiceDateTypeFilter {
    return state.filters.dateTypeFilterIndex;
  }

  @Selector()
  static getDateRange(state: InvoicingDataStateModel): Interval {
    return state.filters.dateRange;
  }

  @Selector()
  static selectedActivitiesCount(state: InvoicingDataStateModel): number {
    return state.selectedActivities.length;
  }

  @Selector()
  static selectedActivityIds(state: InvoicingDataStateModel): string[] {
    return state.selectedActivities
      .map((activity) => activity.id)
      .filter((id): id is string => id !== undefined);
  }

  @Selector()
  static selectedActivities(state: InvoicingDataStateModel): Activity[] {
    return state.selectedActivities;
  }

  @Selector([InvoicingDataState.selectedActivityIds])
  static selectedActivityIdSet(ids: string[]): ReadonlySet<string> {
    return new Set(ids);
  }

  @Selector()
  static selectedClientId(state: InvoicingDataStateModel): string | null {
    return state.selectedClientId;
  }

  @Selector()
  static selectedClientName(state: InvoicingDataStateModel): string | null {
    return state.selectedClientName;
  }

  @Selector()
  static allSelectedActivitiesHaveSameAgreement(
    state: InvoicingDataStateModel,
  ): boolean {
    const selectedActivities = state.selectedActivities;
    return selectedActivities.every(
      (activity) => activity.agreementId === selectedActivities[0].agreementId,
    );
  }

  @Selector()
  static stateOfSelectedActivities(
    state: InvoicingDataStateModel,
  ): SelectedActivityChecks {
    return state.selectedActivityStates;
  }

  @Selector()
  static getFilters(state: InvoicingDataStateModel): InvoiceFilters {
    return state.filters;
  }

  @Selector()
  static getPagination(state: InvoicingDataStateModel): Pagination | null {
    return state.pagination;
  }

  @Selector()
  static getPageSize(state: InvoicingDataStateModel): InvoicingGridPageSizes {
    return state.pageSize;
  }

  @Selector()
  static pageSizes(state: InvoicingDataStateModel): InvoicingGridPageSizes[] {
    return state.pageSizes;
  }

  @Selector()
  static cacheBlockSize(
    state: InvoicingDataStateModel,
  ): InvoicingGridPageSizes {
    return state.cacheBlockSize;
  }

  @Action(InvoicingSetDateTypeFilterIndex)
  invoicingSetDateTypeFilterIndex(
    ctx: StateContext<InvoicingDataStateModel>,
    action: InvoicingSetDateTypeFilterIndex,
  ): void {
    ctx.setState(
      patch({
        filters: patch({
          dateTypeFilterIndex: action.index,
        }),
      }),
    );
  }

  @Action(InvoicingSetTimeRange)
  timelineSetDate(
    ctx: StateContext<InvoicingDataStateModel>,
    action: InvoicingSetTimeRange,
  ): void {
    const state = ctx.getState();
    ctx.patchState({
      filters: {
        ...state.filters,
        dateRange: action.interval,
      },
    });
  }

  @Action(InvoicingSetAgreements)
  invoicingSetAgreements(
    ctx: StateContext<InvoicingDataStateModel>,
    action: InvoicingSetAgreements,
  ): void {
    const state = ctx.getState();
    ctx.patchState({
      filters: {
        ...state.filters,
        agreements: action.agreements,
      },
    });
  }

  @Action(InvoicingSetEmployees)
  invoicingSetEmployees(
    ctx: StateContext<InvoicingDataStateModel>,
    action: InvoicingSetEmployees,
  ): void {
    const state = ctx.getState();
    ctx.patchState({
      filters: {
        ...state.filters,
        employees: action.employees,
      },
    });
  }

  @Action(InvoicingSetShiftCategories)
  invoicingSetShiftCategories(
    ctx: StateContext<InvoicingDataStateModel>,
    action: InvoicingSetShiftCategories,
  ): void {
    const state = ctx.getState();
    ctx.patchState({
      filters: {
        ...state.filters,
        shiftCategories: action.shiftCategories,
      },
    });
  }

  @Action(InvoicingSetActivityCategories)
  invoicingSetActivityCategories(
    ctx: StateContext<InvoicingDataStateModel>,
    action: InvoicingSetActivityCategories,
  ): void {
    const state = ctx.getState();
    ctx.patchState({
      filters: {
        ...state.filters,
        activityCategories: action.activityCategories,
      },
    });
  }

  @Action(InvoicingSetPartners)
  InvoicingSetPartners(
    ctx: StateContext<InvoicingDataStateModel>,
    action: InvoicingSetPartners,
  ): void {
    const state = ctx.getState();
    ctx.patchState({
      filters: {
        ...state.filters,
        partners: action.partners,
      },
    });
  }

  @Action(InvoicingSetJobs)
  InvoicingSetJobs(
    ctx: StateContext<InvoicingDataStateModel>,
    action: InvoicingSetJobs,
  ): void {
    const state = ctx.getState();
    ctx.patchState({
      filters: {
        ...state.filters,
        jobId: action.jobIds,
      },
    });
  }

  @Action(InvoicingSetUserLabels)
  InvoicingSetUserLabels(
    ctx: StateContext<InvoicingDataStateModel>,
    action: InvoicingSetUserLabels,
  ): void {
    const state = ctx.getState();
    ctx.patchState({
      filters: {
        ...state.filters,
        shiftUserLabelIds: action.shiftUserLabelIds,
      },
    });
  }

  @Action(InvoicingSetShiftLabels)
  InvoicingSetShiftLabels(
    ctx: StateContext<InvoicingDataStateModel>,
    action: InvoicingSetShiftLabels,
  ): void {
    const state = ctx.getState();
    ctx.patchState({
      filters: {
        ...state.filters,
        shiftLabelsIds: action.shiftLabelIds,
      },
    });
  }

  @Action(InvoicingSetShiftProjects)
  InvoicingSetShiftProjects(
    ctx: StateContext<InvoicingDataStateModel>,
    action: InvoicingSetShiftProjects,
  ): void {
    const state = ctx.getState();
    ctx.patchState({
      filters: {
        ...state.filters,
        shiftProjectIds: action.shiftProjectIds,
      },
    });
  }

  @Action(InvoicingSetShiftProfessions)
  InvoicingSetShiftProfessions(
    ctx: StateContext<InvoicingDataStateModel>,
    action: InvoicingSetShiftProfessions,
  ): void {
    const state = ctx.getState();
    ctx.patchState({
      filters: {
        ...state.filters,
        profession: action.professions,
      },
    });
  }

  @Action(InvoicingSetEmployeeOrgUnits)
  InvoicingSetEmployeeOrgUnits(
    ctx: StateContext<InvoicingDataStateModel>,
    action: InvoicingSetEmployeeOrgUnits,
  ): void {
    const state = ctx.getState();
    ctx.patchState({
      filters: {
        ...state.filters,
        orgUnit: action.orgUnit,
      },
    });
  }

  @Action(InvoicingSetActivityStatus)
  invoicingSetActivityStatus(
    ctx: StateContext<InvoicingDataStateModel>,
    action: InvoicingSetActivityStatus,
  ): void {
    const state = ctx.getState();
    ctx.patchState({
      filters: {
        ...state.filters,
        invoiced: action.status,
      },
    });
  }

  @Action(InvoicingSetSearchString)
  invoicingSetSearchString(
    ctx: StateContext<InvoicingDataStateModel>,
    action: InvoicingSetSearchString,
  ): void {
    const state = ctx.getState();
    ctx.patchState({
      filters: {
        ...state.filters,
        searchString: action.searchString,
      },
    });
  }

  @Action(InvoicingFilterOnlyServiceActivities)
  InvoicingFilterOnlyServiceActivities(
    ctx: StateContext<InvoicingDataStateModel>,
    action: InvoicingFilterOnlyServiceActivities,
  ): void {
    const state = ctx.getState();
    ctx.patchState({
      filters: {
        ...state.filters,
        showServiceActivities: action.showServiceActivities,
      },
    });
  }

  @Action(InvoicingSetPageSize)
  invoicingSetPageSize(
    ctx: StateContext<InvoicingDataStateModel>,
    action: InvoicingSetPageSize,
  ): void {
    if (
      ctx
        .getState()
        .pageSizes.includes(action.newPageSize as InvoicingGridPageSizes)
    ) {
      ctx.patchState({
        pageSize: action.newPageSize as InvoicingGridPageSizes,
      });
    } else {
      console.error('[InvoicingDataState] Invalid pagesize provided');
    }
  }

  @Action(InvoicingSetCacheBlockSize)
  invoicingSetCacheBlockSize(
    ctx: StateContext<InvoicingDataStateModel>,
    action: InvoicingSetCacheBlockSize,
  ): void {
    if (
      ctx
        .getState()
        .pageSizes.includes(action.cacheBlockSize as InvoicingGridPageSizes)
    ) {
      ctx.patchState({
        cacheBlockSize: action.cacheBlockSize as InvoicingGridPageSizes,
      });
    } else {
      console.error('[InvoicingDataState] Invalid cacheBlockSize provided');
    }
  }

  @Action(InvoicingSetPagination)
  changePagination(
    ctx: StateContext<InvoicingDataStateModel>,
    action: InvoicingSetPagination,
  ): void {
    ctx.patchState({
      pageNumber: action.pageNumber,
    });
  }

  @Action(InvoicingSetSelectedActivity)
  setSelectedActivityIds(
    ctx: StateContext<InvoicingDataStateModel>,
    action: InvoicingSetSelectedActivity,
  ): void {
    ctx.patchState({
      selectedActivities: action.activities,
    });
  }

  @Action(InvoicingSetClient)
  invoicingSetClient(
    ctx: StateContext<InvoicingDataStateModel>,
    action: InvoicingSetClient,
  ): void {
    ctx.patchState({
      selectedClientId: action.client.id,
      selectedClientName: action.client.name,
    });
  }

  @Action(SetSelectedActivities)
  addStateOfSelectedActivities(
    ctx: StateContext<InvoicingDataStateModel>,
    action: SetSelectedActivities,
  ): void {
    ctx.patchState({
      selectedActivityStates: action.activitiesStates,
    });
  }

  @Action(InvoicingFetchShifts)
  fetchUsers(
    ctx: StateContext<InvoicingDataStateModel>,
    action: InvoicingFetchShifts,
  ): Observable<
    | ManyEntity<Activity>
    | {
        activities: Activity[];
        pagination: Pagination | undefined;
      }
  > {
    const state = ctx.getState();
    const pageToFetch = Math.floor(action.startRow / state.cacheBlockSize) + 1;

    if (action.hasWilsonShareAccess) {
      return this.fetchShiftsWithSharedActivities(pageToFetch, state, ctx);
    } else {
      return this.fetchShiftsActivities(pageToFetch, state, ctx);
    }
  }

  private fetchAndTransformJobActivities(
    startDate: number | Date,
    endDate: number | Date,
    relations: string[],
  ): Observable<MappedActivitySelectionItems> {
    return this.requestForProposalGatewayService
      .getRequestsForProposalsInRange({
        relations,
        where: {
          startDate: new Date(startDate).toISOString(),
          endDate: new Date(endDate).toISOString(),
        },
      })
      .pipe(
        map(
          (
            requestForProposals: RequestForProposal[],
          ): MappedActivitySelectionItems => {
            return {
              data: flatten(
                requestForProposals.map(
                  (request): ActivitySelectionListItem[] => {
                    const job = request.job as ActivitiesJob;
                    return job.activities.map(
                      (activity): ActivitySelectionListItem => ({
                        ...(activity as Activity),
                        requestForProposalId: request?.id as string,
                        jobName: job.name,
                        jobOriginOrgUnit: job.organizationalUnit
                          ?.name as string,
                        jobId: job.id as string,
                        jobOrganizationalUnitId:
                          job.organizationalUnitId as string,
                      }),
                    );
                  },
                ),
              ),
            };
          },
        ),
        catchError((): Observable<MappedActivitySelectionItems> => {
          this.message.error(this.translate.instant('general.error'));
          return of({ data: [] });
        }),
      );
  }

  private generateActivityView(
    activity: Activity & {
      shift: Shift & { user: User };
      activityCategory: ActivityCategory;
      invoices: ActivityInvoiceModel[];
      jobName?: string;
      jobOriginOrgUnit?: string;
      requestForProposalId?: string;
    },
  ): ActivityViewModel {
    const shift = activity.shift;
    return {
      id: activity.id,
      originalActivity: { ...activity, shift },
      user: `${shift.user.firstName} ${shift.user.lastName}`,
      jobName: activity?.jobName,
      jobOriginOrgUnit: activity?.jobOriginOrgUnit,
      requestForProposalId: activity?.requestForProposalId,
      activityName: this.translateActivityCategoryService.resolve(
        activity.activityCategory,
      ),
      agreementDetails: {
        clientName: activity.agreement?.client?.name,
        agreementName: activity.agreement?.name,
        costCenter: activity.agreement?.costCenter,
      },
      shiftDetails: {
        shiftName: shift.name,
        shiftId: shift.id as string,
        shiftReportedStartDate: shift.reportedStartDatetime ?? null,
      },
      formattedStartDateTime: `${this.localizedDateTimeFormat.toFormattedSignal(
        activity.startDatetime,
      )()}`,
      serviceDetails: {
        serviceName: activity?.service?.name as string,
        serviceId: `${activity?.service?.id}`,
      },
      invoices: activity.invoices,
    };
  }

  private fetchShiftsActivities(
    pageToFetch: number,
    state: InvoicingDataStateModel,
    ctx: StateContext<InvoicingDataStateModel>,
  ): Observable<ManyEntity<Activity>> {
    return this.activitiesGateway
      .getActivities({
        options: this.invoiceDataHelperService.buildActivitiesOptions(
          state.filters,
          pageToFetch,
          state.cacheBlockSize,
        ),
      })
      .pipe(
        catchError(() => {
          this.message.error(
            this.translate.instant('general.something_went_wrong'),
          );
          return of({
            data: [],
            count: 0,
            pagination: defaults.pagination || undefined,
          });
        }),
        tap((activitiesFromServer) => {
          const activities = [...activitiesFromServer.data].filter(
            (activity) => !!activity.shift && !!activity.shift.user,
          );
          const processedActivities: ActivityViewModel[] = activities.map(
            (activity) =>
              this.generateActivityView(
                activity as Activity & {
                  shift: Shift & { user: User };
                  activityCategory: ActivityCategory;
                  invoices: ActivityInvoiceModel[];
                },
              ),
          );

          ctx.patchState({
            activities: processedActivities,
            pagination: activitiesFromServer.pagination,
          });
        }),
      );
  }

  private fetchShiftsWithSharedActivities(
    pageToFetch: number,
    state: InvoicingDataStateModel,
    ctx: StateContext<InvoicingDataStateModel>,
  ): Observable<{
    activities: Activity[];
    pagination: Pagination | undefined;
  }> {
    return combineLatest([
      this.activitiesGateway.getActivities({
        options: this.invoiceDataHelperService.buildActivitiesOptions(
          state.filters,
          pageToFetch,
          state.cacheBlockSize,
        ),
      }),
      this.fetchAndTransformJobActivities(
        state.filters.dateRange.start,
        state.filters.dateRange.end,
        this.invoiceDataHelperService.JOB_RELATIONS,
      ),
    ]).pipe(
      map(
        ([
          { data: myActivities, pagination: pagination },
          { data: jobActivities },
        ]) => {
          const activities = myActivities.map((activity) => {
            const activityWithJob = jobActivities.find(
              (job) => job.id === activity.id,
            );

            if (activityWithJob) {
              return {
                ...activity,
                jobName: activityWithJob.jobName,
                jobOriginOrgUnit: activityWithJob.jobOriginOrgUnit,
                requestForProposalId: activityWithJob.requestForProposalId,
              };
            } else {
              return activity;
            }
          });

          return {
            activities,
            pagination,
          };
        },
      ),
      tap((activitiesFromServer) => {
        const activities = [...activitiesFromServer.activities].filter(
          (activity) => !!activity.shift && !!activity.shift.user,
        );

        const processedActivities: ActivityViewModel[] = activities.map(
          (activity) =>
            this.generateActivityView(
              activity as Activity & {
                shift: Shift & { user: User };
                activityCategory: ActivityCategory;
                invoices: ActivityInvoiceModel[];
              },
            ),
        );

        ctx.patchState({
          activities: processedActivities,
          pagination: activitiesFromServer.pagination,
        });
      }),
    );
  }
}
